import { OptionType } from "shared";

export const constructOptions = (options: string[]): OptionType[] => {
    return [...options]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
        .map((o) => ({
            value: o,
            label: o,
        }));
};

const PRIORITY_ACCOUNT_CURRENCY_CODES = ["RUB", "USD", "EUR", "CNY"] as const;

/** RUB, USD, EUR, CNY в начале (как в данных), остальные по возрастанию. Сравнение кода без учёта регистра. */
export function sortAccountCurrencyCodesForSelect(codes: string[]): string[] {
    const priority: string[] = [];
    const used = new Set<string>();
    for (const p of PRIORITY_ACCOUNT_CURRENCY_CODES) {
        const found = codes.find((c) => c.toUpperCase() === p);
        if (found !== undefined && !used.has(found)) {
            priority.push(found);
            used.add(found);
        }
    }
    const rest = codes
        .filter((c) => !used.has(c))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
    return [...priority, ...rest];
}

export function constructAccountCurrencyOptions(codes: string[]): OptionType[] {
    return sortAccountCurrencyCodesForSelect(codes).map((o) => ({
        value: o,
        label: o,
    }));
}

const compareSelectStrings = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/** Типы счёта: сначала подписи, где есть «основной» или «осн» (без учёта регистра), по возрастанию; затем остальные по возрастанию. */
export function sortAccountTypeLabelsForSelect(labels: string[]): string[] {
    const primary: string[] = [];
    const other: string[] = [];
    for (const s of labels) {
        if (s == null || String(s) === "") {
            continue;
        }
        const low = String(s).toLowerCase();
        if (low.includes("основной") || low.includes("осн")) {
            primary.push(s);
        } else {
            other.push(s);
        }
    }
    primary.sort(compareSelectStrings);
    other.sort(compareSelectStrings);
    return [...primary, ...other];
}

/** Совпадение с `ya_name` организации «ООО "ЯНДЕКС"» в списке legal orgs. */
export const LEGAL_YANDEX_PRIORITY_ORG_YA_NAME = 'ООО "ЯНДЕКС"';

export function findPriorityYandexOrgYaId(orgs: { ya_id: string; ya_name: string }[]): string | null {
    const found = orgs.find((o) => o.ya_name === LEGAL_YANDEX_PRIORITY_ORG_YA_NAME);
    return found != null ? String(found.ya_id) : null;
}

function compareOptionLabels(a: string, b: string): number {
    return a.localeCompare(b);
}

/**
 * Одна и та же `ya_id` первой (Яндекс), остальные опции по возрастанию label.
 * Для ya_name / ya_full_name / ya_eng_name_short / ya_eng_name_full — один раз считаем id через findPriorityYandexOrgYaId.
 */
export function sortLegalOrgSelectOptionsFirstByYaId(
    options: OptionType[],
    priorityYaId: string | null,
): OptionType[] {
    if (priorityYaId == null || priorityYaId === "") {
        return [...options].sort((a, b) => compareOptionLabels(String(a.label), String(b.label)));
    }
    const pid = String(priorityYaId);
    const priority = options.find((o) => String(o.value) === pid);
    const rest = options
        .filter((o) => String(o.value) !== pid)
        .sort((a, b) => compareOptionLabels(String(a.label), String(b.label)));
    return priority ? [priority, ...rest] : [...options].sort((a, b) => compareOptionLabels(String(a.label), String(b.label)));
}
